import {
	BrokerCommunication,
	GFADocumentPaginatedResponse,
	GFAFullDetails,
	PaginatedResponse,
	PaginationParams,
	Task,
} from "@dune/types";
import { useQuery } from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { gfasQueryKeys } from "../query-keys/gfas.query-keys";

const getGFAConstructionSites = async (
	search?: string,
	brokerSocietyId?: number,
) => {
	const params: { search?: string; brokerSocietyId?: number } = {};
	if (search) params.search = search;
	if (brokerSocietyId != null) params.brokerSocietyId = brokerSocietyId;

	const { data } = await privateAxios.get<GFAFullDetails[]>("/gfa", {
		params,
	});
	return data;
};

export const useGetGFAConstructionSites = (
	search?: string,
	brokerSocietyId?: number,
) => {
	return useQuery({
		queryKey: ["gfa", "list", search, brokerSocietyId],
		queryFn: () => getGFAConstructionSites(search, brokerSocietyId),
	});
};

const getGFAFullDetails = async (id: string) => {
	const { data } = await privateAxios.get<GFAFullDetails>(`/gfa/${id}`);
	return data;
};

export const useGetGFAFullDetails = (id: string) => {
	return useQuery({
		queryKey: gfasQueryKeys.detail(id),
		queryFn: () => getGFAFullDetails(id),
		enabled: !!id,
		retry: false,
	});
};

const getGFACommunications = async (
	gfaId: string,
	options?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	const params = new URLSearchParams();
	if (options?.page) params.append("page", options.page.toString());
	if (options?.limit) params.append("limit", options.limit.toString());
	if (options?.sortBy) params.append("sortBy", options.sortBy);
	if (options?.sortOrder) params.append("sortOrder", options.sortOrder);

	const { data } = await privateAxios.get<
		PaginatedResponse<BrokerCommunication>
	>(`/gfa/${gfaId}/communications?${encodeURIComponent(params.toString())}`);
	return data;
};

export const getGFATasks = async (gfaId: string): Promise<Task[]> => {
	const { data } = await privateAxios.get<Task[]>(`/gfa/${gfaId}/tasks`);
	return data;
};

export const useGetGFATasks = (gfaId: string) => {
	return useQuery({
		queryKey: gfasQueryKeys.tasks(gfaId),
		queryFn: () => getGFATasks(gfaId),
		enabled: !!gfaId,
	});
};

export const getGFADocuments = async (
	gfaId: number,
	pagination?: PaginationParams,
): Promise<GFADocumentPaginatedResponse> => {
	const { data } = await privateAxios.get<GFADocumentPaginatedResponse>(
		`/gfa/${gfaId}/documents`,
		{
			params: pagination,
		},
	);
	return data;
};

export const useGetGFADocuments = (
	gfaId: number,
	pagination?: PaginationParams,
) => {
	return useQuery({
		queryKey: gfasQueryKeys.documents(gfaId, pagination),
		queryFn: () => getGFADocuments(gfaId, pagination),
		enabled: !!gfaId,
	});
};

export const useGetGFACommunications = (
	gfaId: string,
	options?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	return useQuery({
		queryKey: gfasQueryKeys.communications(gfaId, options),
		queryFn: () => getGFACommunications(gfaId, options),
		enabled: !!gfaId,
	});
};

const getGFAUnprocessedCommunicationsCount = async (gfaId: string) => {
	const { data } = await privateAxios.get<{ count: number }>(
		`/gfa/${gfaId}/communications/unprocessed-count`,
	);
	return data.count;
};

export const useGetGFAUnprocessedCommunicationsCount = (gfaId: string) => {
	return useQuery({
		queryKey: gfasQueryKeys.unprocessedCommunicationsCount(gfaId),
		queryFn: () => getGFAUnprocessedCommunicationsCount(gfaId),
		enabled: !!gfaId,
		refetchInterval: 30000, // Refresh every 30 seconds
	});
};

const getGFABinderAmendment = async () => {
	const { data } = await privateAxios.get<{
		commissionRate: number | null;
	}>("/gfa/binder-amendment");
	return data;
};

export const useGetGFABinderAmendment = (enabled = true) => {
	return useQuery({
		queryKey: gfasQueryKeys.binderAmendment(),
		queryFn: getGFABinderAmendment,
		enabled,
		retry: false,
	});
};
